import type { RoutePlan } from '../api/types';

/**
 * What to call a round that nobody has named.
 *
 * A saved route or operation carries the name it was saved under, and that
 * always wins: somebody chose it. An unplanned-for file needs something, and
 * what it used to take was the city and the departure time — "District of
 * Columbia 09:00" — which names the place the round is in rather than the
 * round, and reads identically for every round anybody plans there.
 *
 * The two ends say more in the same space. They are what somebody asked for
 * first, and what they would say out loud if asked where they were going.
 */
export function endsTitle(
  plan: RoutePlan | null | undefined,
  fallback: string,
): string {
  const stops = plan?.stops ?? [];
  const from = stops[0]?.name?.trim() ?? '';
  const to = stops[stops.length - 1]?.name?.trim() ?? '';

  if (!from && !to) return fallback;
  if (!to) return from;
  if (!from) return to;

  /* Departure to return: saying it twice would read as two places. */
  return sameEnd(from, to) ? `${from} round trip` : `${from} to ${to}`;
}

/** The two ends of a multi-day operation, which are days apart. */
export function spanTitle(
  first: RoutePlan | null | undefined,
  last: RoutePlan | null | undefined,
  fallback: string,
): string {
  const from = first?.stops[0]?.name?.trim() ?? '';
  const to = last?.stops[last.stops.length - 1]?.name?.trim() ?? '';

  if (!from && !to) return fallback;
  if (!to || sameEnd(from, to)) return from ? `${from} operation` : fallback;

  return `${from} to ${to}`;
}

function sameEnd(from: string, to: string): boolean {
  return from.toLowerCase() === to.toLowerCase();
}
